import * as ewe from "@eyeo/webext-ad-filtering-solution";
import browser from "webextension-polyfill";
import { EXPERIMENT_FLAG_PREFIX, GET_DIALOG_MESSAGE_TYPE, GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE, DISMISS_DIALOG_MESSAGE_TYPE, STEP_PREF_KEY, TOUCH_POINT_CONFIG_FLAG_PREFIX, TRIAL_START_DATE_PREF_KEY, MS_PER_DAY, } from "../shared/constants.js";
import { DialogAction, } from "../shared/types.js";
import { assertExhaustive } from "../shared/assert-exhaustive.js";
import { buildDialogPayload, findElementByKey } from "./dialog-payload.js";
import { findFlagByPrefix } from "./flags.js";
import { getRemoteOpdConfig, isObject } from "./remote-config.js";
import { buildTextVariables, buildUrlVariables } from "./templating.js";
import { getLastStepId, getStepById, resolveNextTouchPoint, } from "./touchpoints.js";
import { advanceTrialStep, endTrial, startTrial } from "./trial-lifecycle.js";
const serverLogger = new ewe.telemetry.ServerLogger("reverse_trial");
async function toDataUrl(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return `data:${blob.type};base64,${btoa(binary)}`;
}
async function requestDialogHandler(message, params) {
    var _a, _b, _c, _d, _f;
    const flags = await ewe.experiments.getFlags();
    if (!findFlagByPrefix(flags, EXPERIMENT_FLAG_PREFIX)) {
        return null;
    }
    const config = getRemoteOpdConfig(findFlagByPrefix(flags, TOUCH_POINT_CONFIG_FLAG_PREFIX), params.allowedUrlHosts);
    if (!config) {
        return null;
    }
    if (await ewe.account.hasPremium()) {
        const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
        if (currentStep < getLastStepId(config)) {
            await endTrial(params, config);
        }
        return null;
    }
    const lastShown = (_b = params.prefs.get(STEP_PREF_KEY)) !== null && _b !== void 0 ? _b : 0;
    let trialStartDate = (_c = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _c !== void 0 ? _c : 0;
    if (message.ytPremium && !trialStartDate) {
        return null;
    }
    if (!trialStartDate && !config.requireOptIn) {
        await startTrial(params);
        trialStartDate = Date.now();
    }
    const elapsedDays = trialStartDate
        ? Math.floor((Date.now() - trialStartDate) / MS_PER_DAY)
        : 0;
    const step = resolveNextTouchPoint(config, lastShown, elapsedDays);
    if (!step) {
        return null;
    }
    const lastStepId = getLastStepId(config);
    if (message.ytPremium) {
        if (step.id === lastStepId) {
            await endTrial(params, config);
        }
        serverLogger.debug("dialog_skipped_yt_premium", {
            touchPointStep: step.id,
            installDate: (_d = params.prefs.get("installDate")) !== null && _d !== void 0 ? _d : 0,
            reverseTrialStartDate: trialStartDate,
        });
        return null;
    }
    const logoUrl = await toDataUrl(params.logoUrl);
    serverLogger.info("dialog_shown", {
        touchPointStep: step.id,
        installDate: (_f = params.prefs.get("installDate")) !== null && _f !== void 0 ? _f : 0,
        reverseTrialStartDate: trialStartDate,
    });
    const isOptedOut = await params.isOptedOut();
    const urlVars = await buildUrlVariables(step.id, trialStartDate, params, isOptedOut);
    const textVars = buildTextVariables(config, elapsedDays);
    const locale = browser.i18n.getUILanguage();
    return buildDialogPayload(step, logoUrl, locale, urlVars, textVars, params.allowedUrlHosts);
}
function stepHasStartTrial(step) {
    var _a, _b, _c;
    if (!step) {
        return false;
    }
    if (((_a = step.ctaButton) === null || _a === void 0 ? void 0 : _a.action.type) === DialogAction.START_TRIAL) {
        return true;
    }
    return ((_c = (_b = step.links) === null || _b === void 0 ? void 0 : _b.some((l) => l.action.type === DialogAction.START_TRIAL)) !== null && _c !== void 0 ? _c : false);
}
function resolveMessageAction(message, config) {
    var _a;
    if (!config ||
        message.action === DialogAction.CLOSE ||
        message.action === DialogAction.AUTO_DISMISS) {
        return { type: DialogAction.CLOSE };
    }
    const step = getStepById(config, message.touchPointStep);
    const element = findElementByKey(step, message.action);
    return (_a = element === null || element === void 0 ? void 0 : element.action) !== null && _a !== void 0 ? _a : { type: DialogAction.CLOSE };
}
async function dialogActionHandler(message, params) {
    var _a, _b;
    serverLogger.info("dialog_action", {
        action: message.action,
        touchPointStep: message.touchPointStep,
        installDate: (_a = params.prefs.get("installDate")) !== null && _a !== void 0 ? _a : 0,
        reverseTrialStartDate: (_b = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _b !== void 0 ? _b : 0,
    });
    const flags = await ewe.experiments.getFlags();
    const config = getRemoteOpdConfig(findFlagByPrefix(flags, TOUCH_POINT_CONFIG_FLAG_PREFIX), params.allowedUrlHosts);
    const stepAction = resolveMessageAction(message, config);
    const willClose = stepAction.type !== DialogAction.NAVIGATE ||
        stepAction.closeAfter !== false;
    const isOptInStep = config !== null &&
        stepHasStartTrial(getStepById(config, message.touchPointStep));
    if (isOptInStep &&
        willClose &&
        stepAction.type !== DialogAction.START_TRIAL) {
        if (config) {
            await endTrial(params, config);
        }
        await dismissOpenDialogs();
        return;
    }
    switch (stepAction.type) {
        case DialogAction.NAVIGATE:
            if (stepAction.closeAfter === false) {
                return;
            }
            break;
        case DialogAction.END_TRIAL:
            if (config) {
                await endTrial(params, config);
            }
            await dismissOpenDialogs();
            return;
        case DialogAction.START_TRIAL:
            await startTrial(params);
            break;
        case DialogAction.CLOSE:
            break;
        default:
            assertExhaustive(stepAction);
    }
    if (config && message.touchPointStep >= getLastStepId(config)) {
        await endTrial(params, config);
    }
    else {
        await advanceTrialStep(params, message.touchPointStep);
    }
    await dismissOpenDialogs();
}
async function dialogShouldStillShowHandler(message, params) {
    var _a, _b, _c;
    const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
    const shouldStillShow = currentStep < message.touchPointStep;
    serverLogger.debug("dialog_should_still_show_checked", {
        touchPointStep: message.touchPointStep,
        shouldStillShow,
        installDate: (_b = params.prefs.get("installDate")) !== null && _b !== void 0 ? _b : 0,
        reverseTrialStartDate: (_c = params.prefs.get(TRIAL_START_DATE_PREF_KEY)) !== null && _c !== void 0 ? _c : 0,
    });
    return shouldStillShow;
}
async function dismissOpenDialogs() {
    const tabs = await browser.tabs.query({});
    await Promise.all(tabs.map(async (tab) => {
        if (tab.id !== undefined) {
            try {
                await browser.tabs.sendMessage(tab.id, {
                    type: DISMISS_DIALOG_MESSAGE_TYPE,
                });
            }
            catch (_e) {
            }
        }
    }));
}
export function start(params) {
    params.port.on(GET_DIALOG_MESSAGE_TYPE, async (message) => await requestDialogHandler(message !== null && message !== void 0 ? message : {}, params));
    params.port.on(DIALOG_ACTION_MESSAGE_TYPE, async (message) => {
        await dialogActionHandler(message, params);
    });
    params.port.on(GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE, async (message) => await dialogShouldStillShowHandler(message, params));
    const trustedTypes = [
        GET_DIALOG_MESSAGE_TYPE,
        GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE,
        DIALOG_ACTION_MESSAGE_TYPE,
    ];
    params.addTrustedMessageTypes("https://youtube.com", trustedTypes);
    params.addTrustedMessageTypes("https://www.youtube.com", trustedTypes);
    ewe.account.onChanged.addListener(async ({ user, oldUser }) => {
        var _a;
        const becamePremium = (user === null || user === void 0 ? void 0 : user.hasPremium) && !(oldUser === null || oldUser === void 0 ? void 0 : oldUser.hasPremium);
        if (!becamePremium) {
            return;
        }
        const flags = await ewe.experiments.getFlags();
        const rawConfig = findFlagByPrefix(flags, TOUCH_POINT_CONFIG_FLAG_PREFIX);
        if (!rawConfig) {
            return;
        }
        const config = getRemoteOpdConfig(rawConfig, params.allowedUrlHosts);
        if (!config) {
            return;
        }
        const currentStep = (_a = params.prefs.get(STEP_PREF_KEY)) !== null && _a !== void 0 ? _a : 0;
        if (currentStep < getLastStepId(config)) {
            await endTrial(params, config);
            await dismissOpenDialogs();
        }
    });
    ewe.experiments.onChanged.addListener(async () => {
        const flags = await ewe.experiments.getFlags();
        const config = findFlagByPrefix(flags, TOUCH_POINT_CONFIG_FLAG_PREFIX);
        if (!isObject(config)) {
            await ewe.lightningSnippets.clear();
            await params.prefs.reset(STEP_PREF_KEY);
            await params.prefs.reset(TRIAL_START_DATE_PREF_KEY);
        }
    });
}
